if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        // custom by mr shoe
        this._onVariantChanged = this._onVariantChanged.bind(this);
        // custom by mr shoe
        this._didApplyInitialRange = false;
        // custom by mr shoe
        this._allMediaItems = [];
        // custom by mr shoe
        this._allThumbItems = [];
        // custom by mr shoe
        this._currentRangeKey = '';
        // custom by mr shoe
        this._variantImageUrls = [];
      }

      connectedCallback() {
        // custom by mr shoe
        document.addEventListener('variant:changed', this._onVariantChanged);
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      // custom by mr shoe
      disconnectedCallback() {
        document.removeEventListener('variant:changed', this._onVariantChanged);
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        // custom by mr shoe
        this._variantImageUrls = this._parseVariantImageUrls();
        this.setSliderOptions();
        // custom by mr shoe
        this._cacheOriginalMediaNodes();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
        // custom by mr shoe
        if (typeof this.thumbsInstance === 'object') {
          this.thumbsInstance.slider.destroy();
          this.thumbsInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          // custom by mr shoe
          if (!this._didApplyInitialRange) {
            const initialVariant = this._getSelectedVariantFromRoot();
            if (initialVariant) this._applyMediaRangeForVariant(initialVariant);
            this._didApplyInitialRange = true;
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        // custom by mr shoe
        this._applyMediaRangeForVariant(variant);
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      // custom by mr shoe
      _getClosestProductRoot() {
        return this.closest('product-info') || this;
      }

      // custom by mr shoe
      _getSelectedVariantFromRoot() {
        try {
          const root = this._getClosestProductRoot();
          const node = root.querySelector('variant-selects [data-selected-variant]');
          if (!node || !node.textContent) return null;
          return JSON.parse(node.textContent);
        } catch (error) {
          return null;
        }
      }

      // custom by mr shoe
      _onVariantChanged(event) {
        const variant = event?.detail?.variant;
        if (!variant) return;

        const selectedVariant = this._getSelectedVariantFromRoot();
        if (selectedVariant && String(selectedVariant.id || '') !== String(variant.id || '')) return;

        this._applyMediaRangeForVariant(variant);
      }

      // custom by mr shoe
      _getMediaUrlToken(url) {
        if (!url) return '';
        try {
          const parsed = new URL(String(url), window.location.origin);
          const pathname = String(parsed.pathname || '');
          return (pathname.split('/').filter(Boolean).pop() || '').toLowerCase();
        } catch (error) {
          const raw = String(url || '');
          const noQuery = raw.split('?')[0] || raw;
          return (noQuery.split('/').filter(Boolean).pop() || '').toLowerCase();
        }
      }

      // custom by mr shoe
      _getOrderedMainMediaItems() {
        const mediaItems = this._allMediaItems.length
          ? this._allMediaItems
          : Array.from((this.elements?.mediaList || this.querySelector('[id^="Slider-Gallery"]'))?.querySelectorAll('.product__media-item[data-media-id]') || []);
        return mediaItems
          .filter((item) => !item.classList.contains('swiper-slide-duplicate'))
          .sort((a, b) => Number(a.dataset.mediaIndex || 0) - Number(b.dataset.mediaIndex || 0));
      }

      // custom by mr shoe
      _getThumbnailItems() {
        const thumbItems = this._allThumbItems.length
          ? this._allThumbItems
          : Array.from((this.elements?.thumbnails || this.querySelector('[id^="GalleryThumbnails"]'))?.querySelectorAll('.product__thumbs-item[data-target]') || []);
        return thumbItems.sort((a, b) => {
          const aIndex = Number(a.dataset.mediaPosition || 0);
          const bIndex = Number(b.dataset.mediaPosition || 0);
          return aIndex - bIndex;
        });
      }

      // custom by mr shoe
      _cacheOriginalMediaNodes() {
        if (!this._allMediaItems.length) {
          this._allMediaItems = Array.from(
            (this.elements?.mediaList || this.querySelector('[id^="Slider-Gallery"]'))?.querySelectorAll(
              '.product__media-item[data-media-id]'
            ) || []
          )
            .filter((item) => !item.classList.contains('swiper-slide-duplicate'))
            .sort((a, b) => Number(a.dataset.mediaIndex || 0) - Number(b.dataset.mediaIndex || 0));
        }

        if (!this._allThumbItems.length) {
          this._allThumbItems = Array.from(
            (this.elements?.thumbnails || this.querySelector('[id^="GalleryThumbnails"]'))?.querySelectorAll(
              '.product__thumbs-item[data-target]'
            ) || []
          ).sort((a, b) => Number(a.dataset.mediaPosition || 0) - Number(b.dataset.mediaPosition || 0));
        }
      }

      // custom by mr shoe
      _getThumbsTrack() {
        return this.elements?.thumbnails?.querySelector('.swiper-wrapper') || this.querySelector('[id^="Slider-Thumbnails"]');
      }

      // custom by mr shoe
      _parseVariantImageUrls() {
        try {
          const raw = this.dataset.variantImages;
          if (!raw) return [];
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      // custom by mr shoe
      _extractUrlFromBackground(value) {
        const raw = String(value || '').trim();
        if (!raw) return '';
        const match = raw.match(/url\((['"]?)(.*?)\1\)/i);
        return match && match[2] ? match[2] : '';
      }

      // custom by mr shoe
      _getVariantImageSwatchAnchors() {
        const mediaItems = this._getOrderedMainMediaItems();
        if (!mediaItems.length) return [];

        const seen = {};
        const anchors = [];
        const variantImageUrls = Array.isArray(this._variantImageUrls) ? this._variantImageUrls : [];

        if (variantImageUrls.length) {
          variantImageUrls.forEach((url) => {
            const token = this._getMediaUrlToken(url);
            if (!token) return;

            const matchedMedia = mediaItems.find((item) => {
              const src = item.dataset.src || item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
              return this._getMediaUrlToken(src) === token;
            });

            if (!matchedMedia) return;
            const mediaId = String(matchedMedia.dataset.mediaId || '');
            if (!mediaId || seen[mediaId]) return;
            seen[mediaId] = true;
            anchors.push(mediaId);
          });
        }

        if (anchors.length) return anchors;

        const root = this._getClosestProductRoot();
        const swatchLabels = Array.from(
          root.querySelectorAll('variant-selects label[data-swatch-type="variant_image"]')
        );
        if (!swatchLabels.length) return [];

        swatchLabels.forEach((label) => {
          const swatch = label.querySelector('.swatch-color');
          if (!swatch) return;

          const inlineValue = swatch.style.getPropertyValue('--swatch-background');
          const swatchUrl = this._extractUrlFromBackground(inlineValue);
          const swatchToken = this._getMediaUrlToken(swatchUrl);
          if (!swatchToken) return;

          const matchedMedia = mediaItems.find((item) => {
            const src = item.dataset.src || item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
            return this._getMediaUrlToken(src) === swatchToken;
          });

          if (!matchedMedia) return;
          const mediaId = String(matchedMedia.dataset.mediaId || '');
          if (!mediaId || seen[mediaId]) return;
          seen[mediaId] = true;
          anchors.push(mediaId);
        });

        return anchors;
      }

      // custom by mr shoe
      _getVariantAnchorMediaId(variant) {
        if (!variant) return '';

        const featuredId = variant?.featured_media?.id || variant?.featured_image?.id || '';
        if (featuredId) return String(featuredId);

        const variantImageUrl =
          variant?.featured_media?.preview_image?.src || variant?.featured_image?.src || variant?.image?.src || variant?.image || '';
        const variantToken = this._getMediaUrlToken(variantImageUrl);
        if (!variantToken) return '';

        const matchedMedia = this._getOrderedMainMediaItems().find((item) => {
          const src = item.dataset.src || item.querySelector('img')?.currentSrc || item.querySelector('img')?.src || '';
          return this._getMediaUrlToken(src) === variantToken;
        });

        return matchedMedia ? String(matchedMedia.dataset.mediaId || '') : '';
      }

      // custom by mr shoe
      _getMediaRangeIdsForVariant(variant) {
        const mediaItems = this._getOrderedMainMediaItems();
        if (!mediaItems.length) return null;

        const orderedMediaIds = mediaItems.map((item) => String(item.dataset.mediaId || '')).filter(Boolean);
        const anchorMediaId = this._getVariantAnchorMediaId(variant);
        if (!anchorMediaId) return null;

        const startIndex = orderedMediaIds.indexOf(anchorMediaId);
        if (startIndex < 0) return null;

        const anchorMediaIds = this._getVariantImageSwatchAnchors();
        const nextAnchorIndex = anchorMediaIds
          .map((id) => orderedMediaIds.indexOf(id))
          .filter((index) => index > startIndex)
          .sort((a, b) => a - b)[0];

        const endIndex = Number.isFinite(nextAnchorIndex) ? nextAnchorIndex : orderedMediaIds.length;
        const rangeIds = orderedMediaIds.slice(startIndex, endIndex);
        return rangeIds.length ? new Set(rangeIds) : new Set([anchorMediaId]);
      }

      // custom by mr shoe
      _forceActiveMediaById(mediaId) {
        const targetId = String(mediaId || '').trim();
        if (!targetId) return;

        const mediaItems = Array.from(
          (this.elements?.mediaList || this.querySelector('[id^="Slider-Gallery"]'))?.querySelectorAll(
            '.product__media-item[data-media-id]'
          ) || []
        ).filter((item) => !item.classList.contains('swiper-slide-duplicate'));
        const targetItem = mediaItems.find((item) => String(item.dataset.mediaId || '') === targetId);
        if (!targetItem) return;

        const slideIndex = mediaItems.indexOf(targetItem);
        if (this.sliderInstance?.slider && Number.isFinite(slideIndex) && slideIndex >= 0) {
          this.sliderInstance.slider.slideTo(slideIndex, 0, false);
        }

        const thumbItems = Array.from(
          (this.elements?.thumbnails || this.querySelector('[id^="GalleryThumbnails"]'))?.querySelectorAll(
            '.product__thumbs-item[data-target]'
          ) || []
        );
        thumbItems.forEach((item) => {
          item.classList.remove('swiper-slide-thumb-active');
          item.removeAttribute('aria-current');
        });
        const activeThumb = thumbItems.find((item) => String(item.dataset.target || '') === targetId);
        if (activeThumb) {
          activeThumb.classList.add('swiper-slide-thumb-active');
          activeThumb.setAttribute('aria-current', 'true');
        }
      }

      // custom by mr shoe
      _renderMediaRange(visibleIds) {
        const mediaList = this.elements?.mediaList || this.querySelector('[id^="Slider-Gallery"]');
        const thumbsTrack = this._getThumbsTrack();
        if (!mediaList) return;

        const normalizedIds = visibleIds instanceof Set && visibleIds.size
          ? visibleIds
          : new Set(this._getOrderedMainMediaItems().map((item) => String(item.dataset.mediaId || '')).filter(Boolean));
        const rangeKey = Array.from(normalizedIds).join('|');
        if (rangeKey === this._currentRangeKey) return;

        const sliderWasActive = typeof this.sliderInstance === 'object';
        this.destroySlider();

        mediaList.innerHTML = '';
        this._getOrderedMainMediaItems().forEach((item) => {
          const mediaId = String(item.dataset.mediaId || '');
          if (normalizedIds.has(mediaId)) {
            item.hidden = false;
            item.style.display = '';
            item.setAttribute('aria-hidden', 'false');
            mediaList.appendChild(item);
          }
        });

        if (thumbsTrack) {
          thumbsTrack.innerHTML = '';
          this._getThumbnailItems().forEach((item) => {
            const mediaId = String(item.dataset.target || '');
            if (normalizedIds.has(mediaId)) {
              item.hidden = false;
              item.style.display = '';
              item.setAttribute('aria-hidden', 'false');
              thumbsTrack.appendChild(item);
            }
          });
        }

        // custom by mr shoe
        this.elements.mediaItems = Array.from(mediaList.querySelectorAll('.product__media-item[data-media-id]'));
        // custom by mr shoe
        this._currentRangeKey = rangeKey;

        if (sliderWasActive || FoxTheme.config.mqlMobile || this.enableDesktopSlider) {
          this.initSlider();
        }
      }

      // custom by mr shoe
      _applyMediaRangeForVariant(variant) {
        const visibleIds = this._getMediaRangeIdsForVariant(variant);
        const normalizedIds =
          visibleIds instanceof Set && visibleIds.size
            ? visibleIds
            : new Set(this._getOrderedMainMediaItems().map((item) => String(item.dataset.mediaId || '')).filter(Boolean));
        this._renderMediaRange(normalizedIds);

        const preferredMediaId =
          this._getVariantAnchorMediaId(variant) ||
          Array.from(normalizedIds)[0] ||
          '';
        if (!preferredMediaId) return;

        this._forceActiveMediaById(preferredMediaId);
        requestAnimationFrame(() => this._forceActiveMediaById(preferredMediaId));
      }
    }
  );
}
